import React, { useState } from 'react';
import { 
  Play, 
  Users, 
  Clock, 
  Radio, 
  Eye, 
  Calendar,
  MapPin,
  Share2,
  QrCode,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import QRCodeShare from './QRCodeShare';

function StreamCard({ stream, onJoin }) {
  const [showQRShare, setShowQRShare] = useState(false);
  
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViewerCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live':
        return 'bg-red-500';
      case 'scheduled':
        return 'bg-blue-500';
      case 'ended':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleShare = async () => {
    const streamUrl = `${window.location.origin}/viewer/${stream.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: stream.name,
          text: stream.description || 'Watch this live stream',
          url: streamUrl,
        });
      } catch (error) {
        setShowQRShare(true);
      }
    } else {
      setShowQRShare(true);
    }
  };

  return (
    <>
      <div className="card group hover:border-cf-orange/30 transition-all duration-300">
        {/* Stream Thumbnail */}
        <div className="relative mb-4 rounded-lg overflow-hidden bg-gray-700 aspect-video">
          {stream.thumbnail ? (
            <img 
              src={stream.thumbnail} 
              alt={stream.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Radio className="w-12 h-12 text-gray-500" />
            </div>
          )}
          
          {/* Status Badge */}
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-full text-white text-xs font-semibold ${getStatusColor(stream.status)}`}>
              {stream.status === 'live' && (
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
                  LIVE
                </span>
              )}
              {stream.status === 'scheduled' && 'SCHEDULED'}
              {stream.status === 'ended' && 'ENDED'}
            </span>
          </div>
          
          {/* Duration/Viewer Count */}
          {stream.status === 'live' && (
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
              {stream.duration ? formatDuration(stream.duration) : '0:00'}
            </div>
          )}
        </div>

        {/* Stream Info */}
        <div className="space-y-3">
          <div>
            <h3 className="text-lg font-semibold text-white group-hover:text-cf-orange transition-colors duration-200 line-clamp-2">
              {stream.name}
            </h3>
            {stream.description && (
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                {stream.description}
              </p>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>{formatViewerCount(stream.viewers || 0)}</span>
              </div>
              
              {stream.createdAt && (
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(stream.createdAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Host info */}
            {stream.hostName && (
              <div className="flex items-center space-x-1">
                <span className="text-xs">by {stream.hostName}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 pt-2">
            {stream.status === 'live' ? (
              <button
                onClick={onJoin}
                className="flex-1 btn-primary flex items-center justify-center space-x-2"
              >
                <Play className="w-4 h-4" />
                <span>Join Stream</span>
              </button>
            ) : stream.status === 'scheduled' ? (
              <Link
                to={`/viewer/${stream.id}`}
                className="flex-1 btn-secondary flex items-center justify-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>View Details</span>
              </Link>
            ) : (
              <Link
                to={`/viewer/${stream.id}`}
                className="flex-1 btn-secondary flex items-center justify-center space-x-2"
              >
                <Play className="w-4 h-4" />
                <span>Watch Replay</span>
              </Link>
            )}

            {/* Share Buttons */}
            <button
              onClick={handleShare}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors duration-200"
              title="Share Stream"
            >
              <Share2 className="w-4 h-4" />
            </button>

            <button
              onClick={() => setShowQRShare(true)}
              className="p-2 bg-cf-orange hover:bg-orange-600 text-white rounded-lg transition-colors duration-200"
              title="Share QR Code"
            >
              <QrCode className="w-4 h-4" />
            </button>
          </div>

          {/* Multi-host indicator */}
          {stream.meta?.multiHost && stream.meta?.maxHosts > 1 && (
            <div className="flex items-center space-x-2 text-xs text-blue-400 bg-blue-500 bg-opacity-10 border border-blue-500 rounded-lg p-2">
              <Users className="w-3 h-3" />
              <span>Multi-host stream (up to {stream.meta.maxHosts} hosts)</span>
            </div>
          )}
        </div>
      </div>

      {/* QR Share Modal */}
      {showQRShare && (
        <QRCodeShare
          url={`${window.location.origin}/viewer/${stream.id}`}
          title={`Join ${stream.name} on CloudStream`}
          description={stream.description || 'Watch this live stream'}
          streamId={stream.id}
          hostName={stream.hostName}
          onClose={() => setShowQRShare(false)}
        />
      )}
    </>
  );
}

export default StreamCard; 